"use client"

import { useEffect, useState, useSyncExternalStore } from "react"
import { useLiveQuery } from "dexie-react-hooks"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import { offlineDb } from "../../../lib/offline/db"
import { getDbEpoch, subscribeDbEpoch } from "../../../lib/offline/db-epoch"
import { useSyncStatus } from "../../../lib/offline/hooks/use-sync-status"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import {
  getActiveSyncEngine,
  subscribeActiveSyncEngine,
} from "../../../lib/offline/reset"

/**
 * Read-through status of a single pre-order lookup:
 * - `"loading"`   — the local point-read hasn't settled, the pre-order store is
 *                   still cold-starting, or the on-demand fetch is in flight.
 * - `"present"`   — the row is in Dexie (`preOrder` is non-null).
 * - `"not-found"` — the store settled with no row AND the server confirmed it
 *                   has no such visible pre-order. Terminal.
 * - `"offline"`   — the store settled with no row and the on-demand fetch
 *                   couldn't run (offline / network error). NOT terminal: the
 *                   order may well exist server-side; the hook retries by
 *                   itself when connectivity returns.
 */
export type PreOrderLookupStatus =
  | "loading"
  | "present"
  | "not-found"
  | "offline"

export interface UsePreOrderResult {
  preOrder: PreOrder | null
  status: PreOrderLookupStatus
  /**
   * True while the row is locally absent but the hook is still actively
   * resolving it (cold-start pull, on-demand fetch in flight, hydration
   * landing). Lets callers with their own "give up" ceilings (the focus
   * route's grace timer) skip the ceiling while legitimate work is underway,
   * yet still fire it when a read is genuinely stuck.
   */
  resolving: boolean
}

type RowState = "loading" | "present" | "absent"

interface FetchState {
  /** Id this fetch state belongs to — stale states are ignored. */
  forId: string | null
  phase: "idle" | "pending" | "done"
  found: boolean
  offlineMiss: boolean
}

const IDLE_FETCH: FetchState = {
  forId: null,
  phase: "idle",
  found: false,
  offlineMiss: false,
}

/**
 * Pure status derivation — exported for unit tests (same pattern as
 * `deriveResourceSyncStatus`). `fetch` must already be normalized to
 * {@link IDLE_FETCH} when it belongs to another id.
 */
export function derivePreOrderLookupStatus(input: {
  row: RowState
  storeReady: boolean
  hydrate: boolean
  fetch: Pick<FetchState, "phase" | "found" | "offlineMiss">
}): PreOrderLookupStatus {
  if (input.row === "present") return "present"
  if (input.row === "loading") return "loading"
  // Row settled "absent" locally. Never conclude anything while the store is
  // still cold-starting — the row may simply not have streamed in yet.
  if (!input.storeReady) return "loading"
  // Hydration disabled → the local store is the whole answer: not on device.
  if (!input.hydrate) return "not-found"
  // Store settled with no row → the on-demand fetch decides. "idle" means the
  // effect is about to fire (or the engine isn't registered yet) — loading.
  if (input.fetch.phase !== "done") return "loading"
  if (input.fetch.offlineMiss) return "offline"
  // Hydration landed; the reactive read is about to flip to "present".
  if (input.fetch.found) return "loading"
  return "not-found"
}

function subscribeOnline(callback: () => void): () => void {
  window.addEventListener("online", callback)
  window.addEventListener("offline", callback)
  return () => {
    window.removeEventListener("online", callback)
    window.removeEventListener("offline", callback)
  }
}

/**
 * Generic single pre-order read (STK-1972).
 *
 * Reads the row reactively from Dexie; when the local store has settled
 * without it, automatically fetches it on-demand (`engine.fetchPreOrderById`,
 * which hydrates the PO + its flows into Dexie) so out-of-window deep-links
 * resolve without every caller re-wiring the fallback. The hook:
 *
 *  - waits for the pre-orders resource's cold-start pull to settle before
 *    concluding the row is missing (a cold store reads "absent" for rows that
 *    simply haven't streamed in yet);
 *  - reports `"offline"` — not `"not-found"` — when the fetch couldn't run,
 *    and retries by itself when connectivity returns (the effect re-fires on
 *    the browser's `online` event).
 *
 * `hydrate: false` opts out of the on-demand fetch — a passive Dexie reader.
 * For a caller that KNOWS the row is being delivered by another path (the
 * focus route's just-created orders arrive via SSE) or that defers the
 * hydration decision to a caller with more context, so a missing row doesn't
 * fire a redundant network fetch (and stamp an in-window row `_ephemeral`).
 * Hydration should have ONE owner per id — a second mounted hydrating caller
 * for the same id would fire its own duplicate fetch. With hydration off, a
 * settled local miss reads `"not-found"` = "not on device" — there is no
 * server-confirmed distinction to make.
 */
export function usePreOrder(
  id: string | null | undefined,
  { hydrate = true }: { hydrate?: boolean } = {}
): UsePreOrderResult {
  // DB epoch in the deps so a reopen (cross-tab reset) re-subscribes the live
  // query on the fresh Dexie connection — same guard as the reactive adapter.
  const epoch = useSyncExternalStore(subscribeDbEpoch, getDbEpoch, getDbEpoch)

  // Tri-state point read. `useLiveQuery` keeps the PREVIOUS resolved value for
  // a tick after `id` changes, so the resolution carries its own `queryId` and
  // anything stale reads as "loading" rather than the previous order's state.
  const lookup = useLiveQuery(
    async (): Promise<{ queryId: string; row: PreOrder | null } | null> => {
      if (!id) return null
      const row = await offlineDb.table<PreOrder>(PRE_ORDER_TABLE).get(id)
      return { queryId: id, row: row ?? null }
    },
    [id, epoch],
    undefined
  )
  const settled = lookup !== undefined && (!id || lookup?.queryId === id)
  const preOrder = settled && id ? (lookup?.row ?? null) : null
  const row: RowState = !settled ? "loading" : preOrder ? "present" : "absent"

  // Cold-start gate: until the pre-orders resource settles (`ready`/`error`),
  // an absent row means "hasn't streamed in yet", not "missing". `error`
  // counts as settled — the on-demand fetch is exactly the recovery path.
  const syncStatus = useSyncStatus("pre-orders")
  const storeReady = syncStatus === "ready" || syncStatus === "error"

  // Track the globally-registered SyncEngine reactively. On a cold deep-link
  // mount this hook can run before the provider effect registers the engine
  // (child effects run before parent effects); subscribing re-renders when the
  // engine lands and the fetch effect re-fires.
  const engine = useSyncExternalStore(
    subscribeActiveSyncEngine,
    getActiveSyncEngine,
    () => null
  )

  // Browser connectivity as reactive state. In the effect's deps, so an
  // offline miss is retried the moment the connection returns.
  const isOnline = useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true
  )

  const [fetchState, setFetchState] = useState<FetchState>(IDLE_FETCH)

  const shouldFetch = hydrate && Boolean(id) && row === "absent" && storeReady
  useEffect(() => {
    if (!shouldFetch || !id) {
      setFetchState(IDLE_FETCH)
      return
    }
    if (!engine) {
      // Engine not registered YET — a transient single-flush window, never a
      // failure. Report "pending" so the status stays "loading"; the engine
      // subscription re-renders and re-fires this effect when it lands.
      setFetchState({
        forId: id,
        phase: "pending",
        found: false,
        offlineMiss: false,
      })
      return
    }

    let cancelled = false
    setFetchState({
      forId: id,
      phase: "pending",
      found: false,
      offlineMiss: false,
    })
    engine
      .fetchPreOrderById(id)
      .then((res) => {
        if (!cancelled)
          setFetchState({
            forId: id,
            phase: "done",
            found: res.found,
            offlineMiss: res.offlineMiss,
          })
      })
      .catch(() => {
        if (!cancelled)
          setFetchState({
            forId: id,
            phase: "done",
            found: false,
            offlineMiss: true,
          })
      })
    return () => {
      cancelled = true
    }
    // `isOnline` is a deliberate dep: an offline miss re-fetches on reconnect.
  }, [id, shouldFetch, engine, isOnline])

  // No id ⇒ nothing to resolve — a settled miss, never a spinner. (Computed
  // after the hooks above so the call order stays stable across renders.)
  const status: PreOrderLookupStatus = !id
    ? "not-found"
    : derivePreOrderLookupStatus({
        row,
        storeReady,
        hydrate,
        fetch: fetchState.forId === id ? fetchState : IDLE_FETCH,
      })
  // "Absent locally but still loading" is exactly the set of legit-progress
  // states (cold-start / fetch in flight / hydration landing); a point-read
  // that never settles stays row="loading" and is NOT resolving.
  const resolving = status === "loading" && row === "absent"
  return { preOrder, status, resolving }
}
